import React, { useState } from 'react';
import { Fetcher } from '@this/src/util';
import { Select } from '@this/shared/ui/inputs/select';

export interface MembershipResponseArgs {
  organization_id: number;
  organization_name: string;
}

export interface CurrentMembershipResponseArgs {
  enabled: boolean;
  current_organization_id?: number;
  memberships: MembershipResponseArgs[];
}

const LOCKED_PATHS = [
  /^\/select/,
  /reserve_confirm/,
  /draft_trips\/new/,
  /trips\/new/,
  /^\/trips\/\d+/,
  /^\/trips\/simple_request/,
  /^\/trips\/ai_messages\/new/
];

interface Props {
  currentMembership: CurrentMembershipResponseArgs | null;
  className?: string;
}

const OrganizationSwitcher = ({ currentMembership, className }: Props) => {
  const [switching, setSwitching] = useState(false);
  const locked = LOCKED_PATHS.some(pattern => pattern.test(window.location.pathname));

  const handleChange = async (organizationId: number) => {
    if (!currentMembership || organizationId === currentMembership.current_organization_id) {
      return;
    }

    setSwitching(true);
    try {
      await Fetcher.put('/current_membership.json', { organization_id: organizationId });
      location.reload();
    } catch {
      setSwitching(false);
    }
  };

  if (!currentMembership || !currentMembership.enabled || currentMembership.memberships.length <= 1) {
    return null;
  }

  const currentOrganization = currentMembership.memberships.find(
    membership => membership.organization_id === currentMembership.current_organization_id
  );

  const caption = () => {
    if (locked) {
      return 'この画面では組織を切り替えられません';
    }
    return switching ? '切り替えています…' : '組織を切り替える';
  };

  return (
    <div className={className} style={containerStyle}>
      <div style={labelStyle}>ログイン中の組織</div>
      {currentOrganization && (
        <div style={nameStyle} data-wovn-ignore>
          {currentOrganization.organization_name}
        </div>
      )}
      <Select
        fullWidth
        size="small"
        value={currentMembership.current_organization_id}
        disabled={switching || locked}
        onChange={e => handleChange(Number(e.target.value))}
        style={selectStyle}
      >
        {currentMembership.memberships.map(membership => (
          <option key={membership.organization_id} value={membership.organization_id}>
            {membership.organization_name}
          </option>
        ))}
      </Select>
      <div style={captionStyle}>{caption()}</div>
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  marginTop: '14px',
  padding: '12px 14px',
  textAlign: 'left',
  backgroundColor: '#fff',
  border: '1px solid #e5e5e5',
  borderRadius: '8px'
};

const labelStyle: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 'bold',
  color: '#999',
  marginBottom: '4px'
};

const nameStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#333',
  marginBottom: '8px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const selectStyle: React.CSSProperties = {
  fontSize: '12px',
  backgroundColor: '#fff'
};

const captionStyle: React.CSSProperties = {
  fontSize: '10px',
  color: '#999',
  marginTop: '6px'
};

export default OrganizationSwitcher;
